import React, { useMemo } from "react";

import { GlobeIcon, GoogleIcon, SpinnerIcon } from "@/components/icons";

import {
  useGoogleReport,
  type GoogleAdsReport,
  type GoogleBusinessProfileReport,
  type GooglePace,
  type GoogleToDate,
  type GoogleWebsiteReport,
} from "../hooks/useGoogleReport";
import {
  formatDayMonth,
  formatEuroMinor,
  formatPoints,
  formatWindows,
  isAfterDay,
  metricChange,
  pointsChange,
} from "../utils/metricChange";
import {
  ChangePill,
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import {
  BarList,
  QuietList,
  SOURCE_COLOR,
  SourceBadge,
  Sparkline,
  SplitBar,
  VizHeading,
  type QuietRow,
} from "./ReportViz";
import { PaceChart } from "./PaceChart";
import { dayPips, type DayPip } from "./DayPips";
import {
  campaignEvents,
  campaignLanes,
  campaignsByDay,
  type CampaignSpan,
} from "../utils/dayMarkers";

interface GoogleSectionProps {
  clientId: string;
  year: number;
  month: number;
  /** The one source's panel to draw: a Reporting canvas tile or rail item. */
  only: GoogleSource;
}

export type GoogleSource = "businessProfile" | "website" | "googleAds";

const TITLE: Record<GoogleSource, string> = {
  businessProfile: "Business Profile",
  website: "Website",
  googleAds: "Google Ads",
};

const ICON = {
  businessProfile: (
    <SourceBadge color={SOURCE_COLOR.businessProfile}>
      <GoogleIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  ),
  website: (
    <SourceBadge color={SOURCE_COLOR.website}>
      <GlobeIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  ),
  googleAds: (
    <SourceBadge color={SOURCE_COLOR.paid}>
      <GoogleIcon className="w-3.5 h-3.5" />
    </SourceBadge>
  ),
};

/** A Google panel of /reporting, the one `only` names: Business Profile,
 *  Website (GA4) or Google Ads for the month, each led by its pace chart,
 *  then its two or three key figures against the month before, or on a
 *  month in progress against the same days of it. Folded, each shows its
 *  headline figure and a sparkline of it by day.
 *
 *  One fetch, cached per client and month, serves all three panels. A panel
 *  never hides: an unlinked source names the missing link, a linked one
 *  with no rows says so. */
export const GoogleSection: React.FC<GoogleSectionProps> = ({
  clientId,
  year,
  month,
  only,
}) => {
  const { data, isLoading, error } = useGoogleReport(clientId, year, month);
  // The month's Google Ads campaigns by cost: lanes under the Business
  // Profile and Website charts, start and end pips on the Ads chart.
  const campaigns = useMemo<CampaignSpan[]>(
    () =>
      (data?.googleAds?.campaigns ?? []).map((c) => ({
        key: c.campaignId,
        name: c.name,
        startDate: c.startDate,
        endDate: c.endDate,
        days: c.daily.map((d) => d.date),
        channelType: c.channelType,
      })),
    [data],
  );
  const pips = useMemo(
    () => dayPips([], campaignsByDay(campaignEvents(campaigns), year, month)),
    [campaigns, year, month],
  );

  if (isLoading || error || !data) {
    const loadingRow = (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
        <SpinnerIcon className="w-4 h-4" />
        Loading Google data…
      </div>
    );
    const body = isLoading ? (
      loadingRow
    ) : (
      <ReportPanelEmpty
        message="Google data is unavailable."
        hint="Could not load the Google report for this month."
      />
    );
    const folded = isLoading ? (
      loadingRow
    ) : (
      <ReportPanelNote>Google data is unavailable.</ReportPanelNote>
    );
    return (
      <ReportPanel title={TITLE[only]} icon={ICON[only]} collapsedBody={folded}>
        {body}
      </ReportPanel>
    );
  }

  switch (only) {
    case "businessProfile":
      return <BusinessProfilePanel gbp={data.businessProfile} year={year} month={month} campaigns={campaigns} />;
    case "website":
      return <WebsitePanel website={data.website} year={year} month={month} campaigns={campaigns} />;
    case "googleAds":
      return <GoogleAdsPanel ads={data.googleAds} year={year} month={month} campaigns={campaigns} pips={pips} />;
  }
};

/** "partial month · final from 5 November" on a month the source has not
 *  settled. Not fetchedThrough: that is the sync night, not the data day. */
const partialSummary = (finalFrom: string): string =>
  `partial month · final from ${formatDayMonth(finalFrom)}`;

/** Header summary for a source: an unlinked account's history first, then
 *  a source past its finalFrom that is still not complete (its sync is
 *  behind), else the partial-month note while the month settles. A month
 *  with same-days windows names them first, since its figures compare them. */
const sourceSummary = (source: {
  linked: boolean;
  complete: boolean;
  finalFrom: string;
  fetchedThrough: string | null;
  toDate: { through: string; previousThrough: string; current: unknown } | null;
}): string | undefined => {
  const windows =
    !source.complete && source.toDate?.current
      ? formatWindows(source.toDate.through, source.toDate.previousThrough)
      : null;
  if (!source.linked) {
    return windows ? `previously linked account · ${windows}` : "previously linked account";
  }
  if (source.complete) return undefined;
  const status = isAfterDay(source.finalFrom)
    ? source.fetchedThrough
      ? `sync behind · last fetched ${formatDayMonth(source.fetchedThrough)}`
      : "sync behind"
    : partialSummary(source.finalFrom);
  return windows ? `${windows} · ${status}` : status;
};

/** What a panel's figures compare: the month against the month before once
 *  it is complete; on a month in progress the same-days windows, so a part
 *  month never reads as a fall. With no window rows yet, values and no
 *  change. */
const comparedPair = <T,>(
  complete: boolean,
  current: T,
  previous: T | null,
  toDate: GoogleToDate<T> | null,
): { shown: T; base: T | null } => {
  if (complete) return { shown: current, base: previous };
  if (toDate?.current) return { shown: toDate.current, base: toDate.previous };
  return { shown: current, base: null };
};

/** A source's headline metric over its same-days windows, for the pace
 *  chart's label. */
const headlineToDate = <T,>(
  toDate: GoogleToDate<T> | null,
  read: (totals: T) => number,
): { through: string; current: number | null; previous: number | null } | null =>
  toDate
    ? {
        through: toDate.through,
        current: toDate.current ? read(toDate.current) : null,
        previous: toDate.previous ? read(toDate.previous) : null,
      }
    : null;

/** The headline metric per day this month, from the pace's running total;
 *  a day missing from the pace is a gap. */
const perDay = (pace: GooglePace): Array<number | null> => {
  const byDay = new Map(pace.current.map((p) => [p.day, p.value]));
  const lastDay = pace.current.reduce((last, p) => Math.max(last, p.day), 0);
  return Array.from({ length: lastDay }, (_, i) => {
    const today = byDay.get(i + 1);
    const before = i === 0 ? 0 : byDay.get(i);
    return today == null || before == null ? null : today - before;
  });
};

interface PanelMonth {
  year: number;
  month: number;
  /** The month's Google Ads campaigns, by cost. */
  campaigns: ReadonlyArray<CampaignSpan>;
}

/** A source's last settled day of the month: the month's last once it is
 *  complete, else its pace's last day (0 before any). */
const settledDayOf = (
  source: { complete: boolean; pace: GooglePace },
  year: number,
  month: number,
): number =>
  source.complete
    ? new Date(year, month, 0).getDate()
    : source.pace.current[source.pace.current.length - 1]?.day ?? 0;

/** The campaigns' lanes, clipped to the source's settled day, and the same
 *  days as 2px lines under its folded sparkline. */
const campaignMarks = (
  campaigns: ReadonlyArray<CampaignSpan>,
  source: { complete: boolean; pace: GooglePace },
  year: number,
  month: number,
) => {
  const lanes = campaignLanes(campaigns, year, month, settledDayOf(source, year, month));
  const spans = lanes.lanes.map((lane) => ({ key: lane.key, from: lane.from - 1, to: lane.to - 1 }));
  return { lanes, spans };
};

/** A source with no rows for the month. An unlinked account gets no new
 *  sync, so it never says the rows are on their way. */
const noRowsNote = (linked: boolean) => (
  <ReportPanelNote>{linked ? "No data for this month yet." : "No data for this month."}</ReportPanelNote>
);

const NoRows: React.FC<{ linked: boolean }> = ({ linked }) =>
  linked ? (
    <ReportPanelEmpty
      message="No data for this month yet."
      hint="Rows appear once the daily Google sync covers this month."
    />
  ) : (
    <ReportPanelEmpty
      message="No data for this month."
      hint="This panel shows history from a previously linked account; no new data will sync."
    />
  );

const HistoryNote: React.FC = () => (
  <p className="text-xs text-amber-600">
    Showing history from a previously linked account; no new data will sync.
  </p>
);

/** A demoted figure with its change pill, for a panel's quiet list. */
const quietRow = (
  label: string,
  value: string,
  change: { value: number; percentage: number | null } | undefined,
  formatChange?: (value: number) => string,
  trend?: "up" | "down" | "neutral",
): QuietRow => ({
  label,
  value,
  aside: change ? <ChangePill change={change} formatChange={formatChange} trend={trend} /> : undefined,
});

const BusinessProfilePanel: React.FC<PanelMonth & { gbp: GoogleBusinessProfileReport | null }> = ({
  gbp,
  year,
  month,
  campaigns,
}) => {
  const icon = ICON.businessProfile;
  if (!gbp) {
    return (
      <ReportPanel
        title="Business Profile"
        icon={icon}
        collapsedBody={<ReportPanelNote>No Business Profile linked.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="No Business Profile linked."
          hint="Link the client's Google Business Profile location to see Maps and Search stats."
        />
      </ReportPanel>
    );
  }
  const summary = sourceSummary(gbp);
  if (!gbp.current) {
    return (
      <ReportPanel
        title="Business Profile"
        icon={icon}
        summary={summary}
        collapsedBody={noRowsNote(gbp.linked)}
      >
        <NoRows linked={gbp.linked} />
      </ReportPanel>
    );
  }
  const { shown: cur, base: prev } = comparedPair(
    gbp.complete,
    gbp.current,
    gbp.previous,
    gbp.toDate,
  );
  const color = SOURCE_COLOR.businessProfile;
  const marks = campaignMarks(campaigns, gbp, year, month);
  const actionRows = [
    quietRow("Calls", cur.callClicks.toLocaleString(), metricChange(cur.callClicks, prev?.callClicks)),
    quietRow(
      "Directions",
      cur.directionRequests.toLocaleString(),
      metricChange(cur.directionRequests, prev?.directionRequests),
    ),
    quietRow(
      "Website clicks",
      cur.websiteClicks.toLocaleString(),
      metricChange(cur.websiteClicks, prev?.websiteClicks),
    ),
    ...(cur.bookings > 0
      ? [quietRow("Bookings", cur.bookings.toLocaleString(), metricChange(cur.bookings, prev?.bookings))]
      : []),
    ...(cur.foodOrders > 0
      ? [
          quietRow(
            "Food orders",
            cur.foodOrders.toLocaleString(),
            metricChange(cur.foodOrders, prev?.foodOrders),
          ),
        ]
      : []),
  ];

  return (
    <ReportPanel
      title="Business Profile"
      icon={icon}
      summary={summary}
      headlineLabel="Actions"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Actions"
              value={cur.actions.toLocaleString()}
              change={metricChange(cur.actions, prev?.actions)}
            />
          }
          chart={
            <Sparkline
              values={perDay(gbp.pace)}
              color={color}
              title="Actions per day this month"
              spans={marks.spans}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {!gbp.linked && <HistoryNote />}
        <PaceChart
          pace={gbp.pace}
          label="Actions"
          color={color}
          complete={gbp.complete}
          toDate={headlineToDate(gbp.toDate, (totals) => totals.actions)}
          year={year}
          month={month}
          lanes={marks.lanes}
        />
        <ReportStats>
          <ReportStat
            label="Actions"
            value={cur.actions.toLocaleString()}
            change={metricChange(cur.actions, prev?.actions)}
          />
          <ReportStat
            label="Impressions"
            value={cur.impressions.toLocaleString()}
            change={metricChange(cur.impressions, prev?.impressions)}
          />
        </ReportStats>
        <QuietList rows={actionRows} />
        {cur.impressions > 0 && (
          <div>
            <VizHeading>Impressions, Maps vs Search</VizHeading>
            <SplitBar
              parts={[
                { label: "Maps", value: cur.impressionsMaps, color },
                { label: "Search", value: cur.impressionsSearch, color: "#A7F3D0" },
              ]}
            />
          </div>
        )}
        {gbp.keywords.length > 0 && (
          <div>
            <VizHeading>Top search keywords</VizHeading>
            <BarList
              color={color}
              rows={gbp.keywords.map((k) => {
                const hidden = k.impressions == null || k.belowThreshold;
                return {
                  key: k.keyword,
                  label: k.keyword,
                  value: hidden ? null : k.impressions,
                  // Google withholds counts below its reporting threshold.
                  display: hidden ? "low" : (k.impressions ?? 0).toLocaleString(),
                };
              })}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};

// GA4's paid channel groups, drawn amber so paid stays apart from organic.
const PAID_CHANNEL = /^(Paid |Display$|Cross-network$)/;

const WebsitePanel: React.FC<PanelMonth & { website: GoogleWebsiteReport | null }> = ({
  website,
  year,
  month,
  campaigns,
}) => {
  const icon = ICON.website;
  if (!website) {
    return (
      <ReportPanel
        title="Website"
        icon={icon}
        collapsedBody={<ReportPanelNote>No Google Analytics property linked.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="No Google Analytics property linked."
          hint="Link the client's GA4 property to see website sessions and key events."
        />
      </ReportPanel>
    );
  }
  const summary = sourceSummary(website);
  const monthTotals = website.current;
  if (!monthTotals) {
    return (
      <ReportPanel
        title="Website"
        icon={icon}
        summary={summary}
        collapsedBody={noRowsNote(website.linked)}
      >
        <NoRows linked={website.linked} />
      </ReportPanel>
    );
  }
  const { shown: cur, base: prev } = comparedPair(
    website.complete,
    monthTotals,
    website.previous,
    website.toDate,
  );
  const color = SOURCE_COLOR.website;
  const marks = campaignMarks(campaigns, website, year, month);
  // Users do not add across days, so a same-days window has none: the users
  // rows keep the month so far, with no change.
  const users = cur.totalUsers ?? monthTotals.totalUsers;
  const newUsers = cur.newUsers ?? monthTotals.newUsers;
  const soFar = cur.totalUsers == null && monthTotals.totalUsers != null ? " so far" : "";
  const hasPaidChannel = website.channels.some((c) => PAID_CHANNEL.test(c.channelGroup));

  return (
    <ReportPanel
      title="Website"
      icon={icon}
      summary={summary}
      headlineLabel="Sessions"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Sessions"
              value={cur.sessions.toLocaleString()}
              change={metricChange(cur.sessions, prev?.sessions)}
            />
          }
          chart={
            <Sparkline
              values={perDay(website.pace)}
              color={color}
              title="Sessions per day this month"
              spans={marks.spans}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {!website.linked && <HistoryNote />}
        <PaceChart
          pace={website.pace}
          label="Sessions"
          color={color}
          complete={website.complete}
          toDate={headlineToDate(website.toDate, (totals) => totals.sessions)}
          year={year}
          month={month}
          lanes={marks.lanes}
        />
        <ReportStats>
          <ReportStat
            label="Sessions"
            value={cur.sessions.toLocaleString()}
            change={metricChange(cur.sessions, prev?.sessions)}
          />
          <ReportStat
            label="Key events"
            value={cur.keyEvents.toLocaleString()}
            change={metricChange(cur.keyEvents, prev?.keyEvents)}
          />
        </ReportStats>
        <QuietList
          rows={[
            quietRow(
              `Users${soFar}`,
              users != null ? users.toLocaleString() : "-",
              metricChange(cur.totalUsers, prev?.totalUsers),
            ),
            quietRow(
              `New users${soFar}`,
              newUsers != null ? newUsers.toLocaleString() : "-",
              metricChange(cur.newUsers, prev?.newUsers),
            ),
            quietRow(
              "Engagement rate",
              cur.engagementRate != null ? `${(cur.engagementRate * 100).toFixed(1)}%` : "-",
              pointsChange(cur.engagementRate, prev?.engagementRate),
              formatPoints,
            ),
          ]}
        />
        {website.channels.length > 0 && (
          <div>
            <VizHeading
              aside={
                hasPaidChannel ? (
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: SOURCE_COLOR.paid }}
                    />
                    paid
                  </span>
                ) : undefined
              }
            >
              Sessions by channel
            </VizHeading>
            <ChannelBars channels={website.channels} color={color} />
          </div>
        )}
        {website.keyEvents.length > 0 && (
          <div>
            <VizHeading>Top key events</VizHeading>
            <BarList
              color={color}
              rows={website.keyEvents.map((e) => ({
                key: e.eventName,
                label: e.eventName,
                value: e.keyEvents,
                display: e.keyEvents.toLocaleString(),
              }))}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};

/** Channels by sessions, organic in the source colour and paid in amber. */
const ChannelBars: React.FC<{
  channels: GoogleWebsiteReport["channels"];
  color: string;
}> = ({ channels, color }) => (
  <BarList
    color={color}
    rows={channels.map((c) => ({
      key: c.channelGroup,
      label: c.channelGroup,
      value: c.sessions,
      display: c.sessions.toLocaleString(),
      color: PAID_CHANNEL.test(c.channelGroup) ? SOURCE_COLOR.paid : undefined,
    }))}
  />
);

const GoogleAdsPanel: React.FC<
  PanelMonth & { ads: GoogleAdsReport | null; pips: ReadonlyArray<DayPip> }
> = ({ ads, year, month, campaigns, pips }) => {
  const icon = ICON.googleAds;
  if (!ads) {
    return (
      <ReportPanel
        title="Google Ads"
        icon={icon}
        collapsedBody={<ReportPanelNote>No Google Ads account linked.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="No Google Ads account linked."
          hint="Set the client's Google Ads customer ID in its settings."
        />
      </ReportPanel>
    );
  }
  const summary = sourceSummary(ads);
  if (!ads.current) {
    return (
      <ReportPanel
        title="Google Ads"
        icon={icon}
        summary={summary}
        collapsedBody={noRowsNote(ads.linked)}
      >
        <NoRows linked={ads.linked} />
      </ReportPanel>
    );
  }
  const { shown: cur, base: prev } = comparedPair(
    ads.complete,
    ads.current,
    ads.previous,
    ads.toDate,
  );
  const color = SOURCE_COLOR.paid;
  // Money deltas stay in cents and print as euros with the cents.
  const formatMinorDelta = (minor: number) => formatEuroMinor(minor, true);
  const cpcChange = metricChange(cur.cpcMinor, prev?.cpcMinor);
  // Lower CPC is the win: colour by favorability, the label keeps the raw delta.
  const cpcTrend = !cpcChange
    ? undefined
    : cpcChange.value < 0
    ? "up"
    : cpcChange.value > 0
    ? "down"
    : "neutral";
  const byCost = [...ads.campaigns].sort((a, b) => b.costMinor - a.costMinor);
  const marks = campaignMarks(campaigns, ads, year, month);
  // The pace chart stacks each campaign's clicks.
  const clickLayers = ads.campaigns.map((c) => ({
    key: c.campaignId,
    name: c.name,
    startDate: c.startDate,
    daily: c.daily.map((d) => ({ date: d.date, value: d.clicks })),
  }));

  return (
    <ReportPanel
      title="Google Ads"
      icon={icon}
      summary={summary}
      headlineLabel="Clicks"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Clicks"
              value={cur.clicks.toLocaleString()}
              change={metricChange(cur.clicks, prev?.clicks)}
            />
          }
          chart={
            <Sparkline
              values={perDay(ads.pace)}
              color={color}
              title="Clicks per day this month"
              spans={marks.spans}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {!ads.linked && <HistoryNote />}
        <PaceChart
          pace={ads.pace}
          label="Clicks"
          color={color}
          complete={ads.complete}
          toDate={headlineToDate(ads.toDate, (totals) => totals.clicks)}
          year={year}
          month={month}
          pips={pips}
          campaigns={clickLayers}
        />
        <ReportStats>
          <ReportStat
            label="Spend"
            value={formatEuroMinor(cur.costMinor)}
            change={metricChange(cur.costMinor, prev?.costMinor)}
            formatChange={formatMinorDelta}
          />
          <ReportStat
            label="Clicks"
            value={cur.clicks.toLocaleString()}
            change={metricChange(cur.clicks, prev?.clicks)}
          />
          <ReportStat
            label="Conversions"
            value={cur.conversions.toLocaleString()}
            change={metricChange(cur.conversions, prev?.conversions)}
          />
        </ReportStats>
        <QuietList
          rows={[
            quietRow(
              "Impressions",
              cur.impressions.toLocaleString(),
              metricChange(cur.impressions, prev?.impressions),
            ),
            quietRow(
              "CTR",
              cur.ctr != null ? `${(cur.ctr * 100).toFixed(2)}%` : "-",
              pointsChange(cur.ctr, prev?.ctr),
              formatPoints,
            ),
            quietRow(
              "CPC",
              cur.cpcMinor != null ? formatEuroMinor(cur.cpcMinor, true) : "-",
              cpcChange,
              formatMinorDelta,
              cpcTrend,
            ),
            quietRow(
              "Phone calls",
              cur.phoneCalls.toLocaleString(),
              metricChange(cur.phoneCalls, prev?.phoneCalls),
            ),
          ]}
        />
        {byCost.length > 0 && (
          <div>
            <VizHeading>Spend by campaign</VizHeading>
            <BarList
              color={color}
              rows={byCost.map((c) => ({
                key: c.campaignId,
                label: (
                  <>
                    {c.name}
                    {c.status && (
                      <span className="text-gray-400"> · {c.status.toLowerCase()}</span>
                    )}
                  </>
                ),
                value: c.costMinor,
                display: formatEuroMinor(c.costMinor),
              }))}
            />
          </div>
        )}
      </div>
    </ReportPanel>
  );
};
